import SearchAPI from '../../api/search';
import types from '../mutation-types';
export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  pagination: {
    contacts: {},
    conversations: {},
    messages: {},
  },
  searchState: {
    query: '',
    selectedTab: 'all',
  },
  scrollPosition: 0,
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getPagination(state) {
    return state.pagination;
  },
  getSearchState(state) {
    return state.searchState;
  },
  getSearchScrollPosition(state) {
    return state.scrollPosition;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, { q }) {
    if (!q) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, page: 1 }),
        dispatch('conversationSearch', { q, page: 1 }),
        dispatch('messageSearch', { q, page: 1 }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, { q, page = 1 }) {
    const isLoadMore = page > 1;
    if (!isLoadMore) {
      commit(types.CONTACT_SEARCH_SET, []);
    }
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ q, page });
      commit(types.CONTACT_SEARCH_ADD_RECORDS, {
        records: data.payload.contacts,
        isLoadMore,
      });
      commit(types.SET_SEARCH_PAGINATION, {
        type: 'contacts',
        pagination: data.pagination.contacts,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async conversationSearch({ commit }, { q, page = 1 }) {
    const isLoadMore = page > 1;
    if (!isLoadMore) {
      commit(types.CONVERSATION_SEARCH_SET, []);
    }
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ q, page });
      commit(types.CONVERSATION_SEARCH_ADD_RECORDS, {
        records: data.payload.conversations,
        isLoadMore,
      });
      commit(types.SET_SEARCH_PAGINATION, {
        type: 'conversations',
        pagination: data.pagination.conversations,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async messageSearch({ commit }, { q, page = 1 }) {
    const isLoadMore = page > 1;
    if (!isLoadMore) {
      commit(types.MESSAGE_SEARCH_SET, []);
    }
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ q, page });
      commit(types.MESSAGE_SEARCH_ADD_RECORDS, {
        records: data.payload.messages,
        isLoadMore,
      });
      commit(types.SET_SEARCH_PAGINATION, {
        type: 'messages',
        pagination: data.pagination.messages,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.MESSAGE_SEARCH_SET, []);
    commit(types.CONVERSATION_SEARCH_SET, []);
    commit(types.CONTACT_SEARCH_SET, []);
    commit(types.SET_SEARCH_PAGINATION, { type: 'contacts', pagination: {} });
    commit(types.SET_SEARCH_PAGINATION, {
      type: 'conversations',
      pagination: {},
    });
    commit(types.SET_SEARCH_PAGINATION, { type: 'messages', pagination: {} });
  },
  setSearchState({ commit }, searchState) {
    commit(types.SET_SEARCH_STATE, searchState);
  },
  setSearchScrollPosition({ commit }, scrollPosition) {
    commit(types.SET_SEARCH_SCROLL_POSITION, scrollPosition);
  },
  resetSearchState({ commit }) {
    commit(types.SET_SEARCH_STATE, { query: '', selectedTab: 'all' });
    commit(types.SET_SEARCH_SCROLL_POSITION, 0);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = records;
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = records;
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = records;
  },
  [types.CONTACT_SEARCH_ADD_RECORDS](state, { records, isLoadMore }) {
    state.contactRecords = isLoadMore
      ? [...state.contactRecords, ...records]
      : records;
  },
  [types.CONVERSATION_SEARCH_ADD_RECORDS](state, { records, isLoadMore }) {
    state.conversationRecords = isLoadMore
      ? [...state.conversationRecords, ...records]
      : records;
  },
  [types.MESSAGE_SEARCH_ADD_RECORDS](state, { records, isLoadMore }) {
    state.messageRecords = isLoadMore
      ? [...state.messageRecords, ...records]
      : records;
  },
  [types.SET_SEARCH_PAGINATION](state, { type, pagination }) {
    state.pagination = { ...state.pagination, [type]: pagination };
  },
  [types.SET_SEARCH_STATE](state, searchState) {
    state.searchState = { ...state.searchState, ...searchState };
  },
  [types.SET_SEARCH_SCROLL_POSITION](state, scrollPosition) {
    state.scrollPosition = scrollPosition;
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
